/**
 * OncallOvernightPanel (ONCALL-01) — the morning triage surface for the
 * after-hours answering-service sheet. The 7 AM PDF is parsed into leads
 * (parse-oncall edge fn); this is where the office clears them.
 *
 * The leads live ONLY here until a dispatcher acts — they never touch the
 * customer's record or look like service history until booked (Seth, 2026-06-06).
 * Three outcomes per lead, all reversible:
 *   • Book it       → promotes to a real pending request (then it attaches to
 *                     the customer + flows to the board to schedule)
 *   • Not needed    → closes quietly (customer fixed it / found someone else),
 *                     no service-history footprint
 *   • (auto) Ran    → a same-day call the tech already ran overnight, derived
 *                     from the completed ESC dispatch — shown for awareness so
 *                     nobody asks "did we run this?"
 */
import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Phone,
  CalendarPlus,
  Check,
  X,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Undo2,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useDismissals, useDismiss } from "./dismissals";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { CustomerLink, DispatchLink } from "@/components/links";
import { useNavigateWithBack } from "@/lib/navBack";
import {
  useOncallQueue,
  useResolveOncall,
  useEmpNames,
  oncallCallerWords,
  type PendingTicket,
  type OncallDetail,
} from "@/features/board/queue";
import { CreateDispatchModal } from "@/features/customer-360/CreateDispatchModal";
import { InfoTip } from "@/components/InfoTip";
import { displayCustomerName, titleCaseIfShouty } from "@/lib/format";

/** AFTERHOURS-POLISH-01 (Seth 2026-10-13: "if it matched … more matched to
 *  customer in pegasus"): batch the MIRROR names for every matched lead, so a
 *  qualified row wears the Pegasus record's identity, not the answering
 *  service's spelling. The caller-vs-account chips still show the raw names. */
function useLeadMirrorNames(leads: PendingTicket[]) {
  const custnos = [...new Set(leads.map((t) => t.custno).filter(Boolean))] as string[];
  return useQuery({
    queryKey: ["oncall_mirror_names", custnos.join(",")],
    enabled: custnos.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Map<string, string>> => {
      const { data } = await supabase
        .from("mirror_customer")
        .select("custno, fullname")
        .in("custno", custnos);
      return new Map(
        ((data ?? []) as { custno: string; fullname: string | null }[])
          .map((r) => [r.custno, displayCustomerName(null, r.fullname)] as const)
          .filter(([, n]) => !!n),
      );
    },
  });
}

/** The name a lead row wears: the Pegasus record's when matched, else the
 *  answering service's. */
function leadName(t: PendingTicket, custNames: Map<string, string> | undefined): string {
  return (t.custno ? custNames?.get(t.custno) : null) || t.customer_name || "Unknown caller";
}

const CLASS_META: Record<string, { label: string; chip: string }> = {
  same_day: { label: "Same-day", chip: "bg-red-50 text-red-700 ring-red-100" },
  service_request: {
    label: "Service Request",
    chip: "bg-amber-50 text-amber-700 ring-amber-100",
  },
  plumbing: {
    label: "Plumbing → Tony",
    chip: "bg-sky-50 text-sky-700 ring-sky-100",
  },
  sales: {
    label: "Sales Lead",
    chip: "bg-violet-50 text-violet-700 ring-violet-100",
  },
  hold_for_office: {
    label: "Hold for Office",
    chip: "bg-slate-100 text-slate-600 ring-slate-200",
  },
};
const CLASS_ORDER = [
  "same_day",
  "service_request",
  "plumbing",
  "sales",
  "hold_for_office",
];
const classOf = (tags: string[]) =>
  CLASS_ORDER.find((k) => tags.includes(k)) ?? "hold_for_office";

/** The caller's own words — shared with the board's ticket cards (queue.ts). */
const reasonOf = oncallCallerWords;

/** ONCALL-03 (Seth 2026-09-01): WHEN the call came in, on every row. The
 *  answering service's own stamp ("8/31/2026 4:20:03PM") needs a space before
 *  AM/PM to parse; the email's arrival time is the same moment in clean ISO. */
function cameInAt(t: PendingTicket): Date | null {
  const taken = t.oncall_detail?.taken_at?.replace(/(\d)(AM|PM)\b/i, "$1 $2");
  if (taken) { const d = new Date(taken); if (!isNaN(d.getTime())) return d; }
  const iso = t.source_received_at ?? t.created_at;
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}
function cameInChip(t: PendingTicket): { label: string; hot: boolean } | null {
  const d = cameInAt(t);
  if (!d) return null;
  const hrs = (Date.now() - d.getTime()) / 3600000;
  return {
    label: `Came in ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}${hrs >= 1 ? ` · waiting ${hrs >= 48 ? Math.round(hrs / 24) + "d" : Math.round(hrs) + "h"}` : ""}`,
    hot: hrs >= 16,
  };
}
/** The last field/office stamp appended to the notes ("— Brent 9:12 PM: …"). */
function lastStamp(t: PendingTicket): string | null {
  const lines = (t.notes ?? "").split("\n").map((l) => l.trim()).filter((l) => l.startsWith("— "));
  return lines.length ? lines[lines.length - 1] : null;
}

/**
 * What does the caller actually want? Booking a NEW visit is "Book it"; a
 * cancellation/reschedule/question is about an EXISTING appointment — "Book it"
 * makes no sense there (Seth, 2026-06-06). Derived from the message, like the
 * urgency guess. The real cancel-with-reason action arrives with CANCEL-01.
 */
type Intent = "book" | "cancel" | "reschedule" | "question";
function intentOf(reason: string): Intent {
  const r = reason.toLowerCase();
  if (/\bcancel/.test(r)) return "cancel";
  if (/\breschedul|\bchange (the|my|our|his|her) appoint|\bmove (the|my|our|his|her) appoint|push (it|my|the|back)/.test(r))
    return "reschedule";
  if (/\bquestion|\bwondering|\bchecking|\bwanted to know|\bclarif|\bjust asking/.test(r)) return "question";
  return "book";
}
const INTENT_CHIP: Record<Exclude<Intent, "book">, string> = {
  cancel: "Wants to cancel",
  reschedule: "Wants to reschedule",
  question: "Question",
};

/** A specific date the caller referenced ("cancel appt on June 17") — surfaced
 *  next to the cancel/reschedule chip so the dispatcher sees WHICH visit
 *  without opening anything. */
const MONTHS =
  "january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec";
function referencedDate(msg: string): string | null {
  if (!msg) return null;
  const m =
    msg.match(new RegExp(`\\b(?:${MONTHS})\\.?\\s+\\d{1,2}(?:st|nd|rd|th)?\\b`, "i")) ||
    msg.match(/\b\d{1,2}\/\d{1,2}(?:\/\d{2,4})?\b/);
  return m ? m[0].replace(/\b([a-z])/g, (x) => x.toUpperCase()) : null;
}

interface Night {
  key: string;
  label: string;
  ts: number;
  agoDays: number;
  leads: PendingTicket[];
}

/** Group open leads by the night they came in, so a Monday backlog reads
 *  "Friday / Saturday / Sunday". Oldest night first — the longest-waiting
 *  customer sits at the top. Day boundaries are pinned to Central (the shop's
 *  timezone) so grouping doesn't drift with the viewer's clock or the −05:00
 *  storage offset on source_received_at. */
const SHOP_TZ = "America/Chicago";
const NIGHT_KEY = new Intl.DateTimeFormat("en-CA", {
  timeZone: SHOP_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const NIGHT_LABEL = new Intl.DateTimeFormat("en-US", {
  timeZone: SHOP_TZ,
  weekday: "long",
  month: "short",
  day: "numeric",
});

function groupByNight(leads: PendingTicket[]): Night[] {
  const todayKey = NIGHT_KEY.format(new Date());
  const map = new Map<string, Night>();
  for (const t of leads) {
    const d = new Date(t.source_received_at ?? t.created_at);
    const key = NIGHT_KEY.format(d); // YYYY-MM-DD in Central
    let g = map.get(key);
    if (!g) {
      g = {
        key,
        label: NIGHT_LABEL.format(d),
        ts: Date.parse(key + "T00:00:00Z"),
        agoDays: Math.max(
          0,
          Math.round((Date.parse(todayKey) - Date.parse(key)) / 86400000),
        ),
        leads: [],
      };
      map.set(key, g);
    }
    g.leads.push(t);
  }
  return [...map.values()].sort((a, b) => a.ts - b.ts);
}

export function OncallOvernightPanel() {
  const { data: leads, isLoading } = useOncallQueue();
  const empNames = useEmpNames().data ?? {};
  const custNames = useLeadMirrorNames(leads ?? []).data;
  const dismissals = useDismissals().data ?? new Set<string>();
  // The lead whose Schedule window is open — rendered at PANEL level so it
  // survives the row leaving the triage list when it's booked (Seth 2026-06-24).
  const [sched, setSched] = useState<PendingTicket | null>(null);
  // ASSIGN-AVAIL-01b: the lead leaves triage when the booking LANDS, not when
  // the Schedule window opens — a canceled window used to strand the call
  // (unbooked, counted "booked", on no list: the 10-13 Lindell/Edina vanish).
  const resolveBooked = useResolveOncall();
  // Design-review Wave-1 (#1 move): QUEUE AGING. Old nights collapse + bulk-sweep.
  const [showStale, setShowStale] = useState(false);
  const [confirmSweep, setConfirmSweep] = useState(false);
  const qc = useQueryClient();
  const sweep = useMutation({
    // Mirrors useResolveOncall's "not_needed" patch exactly, batched 10 at a
    // time with ONE invalidation at the end. Every swept lead stays reversible
    // (reopen in the closed list) — nothing is deleted.
    mutationFn: async (tickets: PendingTicket[]) => {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email ?? null;
      const now = new Date().toISOString();
      for (let i = 0; i < tickets.length; i += 10) {
        await Promise.all(
          tickets.slice(i, i + 10).map((t) => {
            const base = (t.tags ?? []).filter(
              (x) => x !== "needs_triage" && x !== "no_service_needed" && x !== "ran_overnight",
            );
            return supabase
              .from("pegasus_dispatch")
              .update({
                status: "cancelled",
                tags: [...base, "no_service_needed"],
                resolved_at: now,
                resolved_by: email,
                updated_at: now,
              })
              .eq("id", t.id);
          }),
        );
      }
    },
    onSuccess: () => {
      setConfirmSweep(false);
      qc.invalidateQueries({ queryKey: ["oncall_queue"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
    },
  });

  if (isLoading && !leads) {
    return <Card className="h-28 animate-pulse bg-slate-100" />;
  }

  const all = leads ?? [];
  const needs = all.filter((t) => (t.tags ?? []).includes("needs_triage"));
  const ranRaw = all.filter(
    (t) =>
      (t.tags ?? []).includes("ran_overnight") &&
      // "Got it" acknowledged rows clear from the morning view (office-wide).
      !dismissals.has(`oncallran:${t.id}`),
  );
  // Two overnight calls about the same job match the same ESC dispatch —
  // show the run ONCE (Seth 2026-08-19, the Hunegs double).
  const seenDispatch = new Set<string>();
  const ran = ranRaw.filter((t) => {
    const d = t.scheduled_dispatch;
    if (!d) return true;
    if (seenDispatch.has(d)) return false;
    seenDispatch.add(d);
    return true;
  });
  const closed = all.filter((t) => (t.tags ?? []).includes("no_service_needed"));
  const booked = all.filter(
    (t) =>
      !(t.tags ?? []).includes("needs_triage") &&
      !(t.tags ?? []).includes("ran_overnight") &&
      !(t.tags ?? []).includes("no_service_needed"),
  );

  // Group the waiting leads by night; urgency-first within each night.
  const sev: Record<string, number> = { asap: 0, soon: 1, routine: 2 };
  const nights = groupByNight(needs);
  for (const n of nights) {
    n.leads.sort(
      (a, b) =>
        (sev[a.urgency ?? "routine"] ?? 2) - (sev[b.urgency ?? "routine"] ?? 2),
    );
  }
  const multiNight = nights.length > 1;
  // Aging policy: after ~a week an after-hours lead is almost always stale (the
  // caller found help elsewhere) — the 4-week pause piled up 188 items across 36
  // nights because the queue had no decay. Older nights collapse into one band
  // with Review / Sweep; fresh nights render as always.
  const STALE_DAYS = 7;
  const freshNights = nights.filter((n) => n.agoDays < STALE_DAYS);
  const staleNights = nights.filter((n) => n.agoDays >= STALE_DAYS);
  const staleLeads = staleNights.flatMap((n) => n.leads);

  if (all.length === 0) {
    return (
      <Card className="flex items-center gap-3 p-5">
        <Phone className="h-5 w-5 text-slate-400" />
        <div>
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            No after-hours calls waiting
            <InfoTip label="About after-hours calls">
              The answering service&apos;s 7 AM summary lands here automatically; calls stay until someone clears them.
            </InfoTip>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
        <span className="font-medium text-slate-600">After-hours queue</span>
        <span aria-hidden>·</span>
        <span>
          {needs.length} waiting
          {multiNight ? ` across ${nights.length} nights` : ""}
        </span>
        {ran.length > 0 && <Pill tone="emerald">{ran.length} ran after-hours</Pill>}
        {booked.length > 0 && <Pill tone="brand">{booked.length} booked</Pill>}
        {closed.length > 0 && <Pill tone="slate">{closed.length} closed</Pill>}
      </div>

      {needs.length === 0 && (
        <Card className="flex items-center gap-3 p-4">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <div className="text-sm text-slate-600">
            Nothing left to triage — every after-hours call is handled.
          </div>
        </Card>
      )}

      {/* One card, stream-style rows (cohesive with "Work the morning",
          Seth 2026-06-11): night dividers are slim in-card rows; each lead
          is a single row — chip, who, their words, actions on the right. */}
      {needs.length > 0 && (
        <Card className="divide-y divide-slate-100 overflow-hidden">
          {/* The aging band: everything older than a week, one row, two exits. */}
          {staleLeads.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 bg-amber-50/70 px-4 py-2.5">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
              <span className="min-w-0 text-xs font-medium leading-relaxed text-amber-900">
                {staleLeads.length} older {staleLeads.length === 1 ? "call" : "calls"} across {staleNights.length} night
                {staleNights.length === 1 ? "" : "s"} ({STALE_DAYS}+ days old) — most callers have found help by now.
              </span>
              <span className="ml-auto flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => { setShowStale((v) => !v); setConfirmSweep(false); }}
                  className="rounded-md border border-amber-200 bg-white px-2 py-1 text-[11px] font-semibold text-amber-800 transition-colors hover:bg-amber-100"
                >
                  {showStale ? "Collapse" : "Review"}
                </button>
                {confirmSweep ? (
                  <>
                    <button
                      type="button"
                      disabled={sweep.isPending}
                      onClick={() => sweep.mutate(staleLeads)}
                      className="rounded-md bg-amber-600 px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
                    >
                      {sweep.isPending ? "Closing…" : `Yes — close ${staleLeads.length} as Not needed`}
                    </button>
                    <button type="button" onClick={() => setConfirmSweep(false)} className="px-1 text-[11px] font-medium text-amber-700 hover:underline">
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmSweep(true)}
                    title="Close every older call as Not needed — no service-history footprint, each one reversible from the closed list"
                    className="rounded-md border border-amber-200 bg-white px-2 py-1 text-[11px] font-semibold text-amber-800 transition-colors hover:bg-amber-100"
                  >
                    Sweep all
                  </button>
                )}
              </span>
            </div>
          )}
          {(showStale ? [...staleNights, ...freshNights] : freshNights).map((n) => (
            <div key={n.key} className="divide-y divide-slate-100">
              {(multiNight || n.agoDays >= 1) && (
                <div className="flex items-center gap-2 bg-slate-50/80 px-4 py-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    {n.label}
                  </span>
                  <span
                    className={cn(
                      "text-[11px]",
                      n.agoDays >= 4 ? "font-semibold text-red-500" : n.agoDays >= 2 ? "font-medium text-amber-600" : "text-slate-400",
                    )}
                  >
                    {n.leads.length} {n.leads.length === 1 ? "call" : "calls"}
                    {n.agoDays >= 1 ? ` · waiting ${n.agoDays}d` : ""}
                  </span>
                </div>
              )}
              {n.leads.map((t) => (
                <OncallRow key={t.id} t={t} empNames={empNames} custNames={custNames} onSchedule={setSched} />
              ))}
            </div>
          ))}
        </Card>
      )}

      {ran.length > 0 && (
        <Card className="divide-y divide-emerald-100/70 overflow-hidden border-emerald-200">
          <div className="bg-emerald-50/70 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
            Ran after-hours — for your awareness
          </div>
          {ran.map((t) => (
            <RanRow key={t.id} t={t} empNames={empNames} custNames={custNames} />
          ))}
        </Card>
      )}

      {closed.length > 0 && <ClosedSection items={closed} empNames={empNames} />}
      {sched && (
        <CreateDispatchModal
          existingTicket={sched}
          custno={sched.custno}
          locno={sched.locno}
          customerName={leadName(sched, custNames)}
          address={sched.address}
          city={titleCaseIfShouty(sched.city)}
          phone={sched.phone}
          onCreated={() =>
            // NOW it's real — promote the lead out of triage. Cancel = nothing
            // changed, the call keeps waiting where it was (ASSIGN-AVAIL-01b).
            resolveBooked.mutate({ id: sched.id, action: "book", tags: sched.tags ?? [] })
          }
          onClose={() => setSched(null)}
        />
      )}
    </div>
  );
}

/** Stream-anatomy lead row (cohesive with TriageStream, Seth 2026-06-11):
 *  urgency dot + class chip + who — their words, signal chips, actions right. */
function OncallRow({
  t,
  empNames,
  custNames,
  onSchedule,
}: {
  t: PendingTicket;
  empNames: Record<string, string>;
  custNames: Map<string, string> | undefined;
  onSchedule: (t: PendingTicket) => void;
}) {
  const navigate = useNavigateWithBack();
  const resolve = useResolveOncall();
  const [showFull, setShowFull] = useState(false);
  const tags = t.tags ?? [];
  const meta = CLASS_META[classOf(tags)];
  const detail = t.oncall_detail;
  // Prefer the full structured message (v8) over the legacy note fragment.
  const reason = detail?.message || reasonOf(t.notes);
  const intent = intentOf(reason);
  const refDate =
    intent === "cancel" || intent === "reschedule" ? referencedDate(reason) : null;
  const review = tags.includes("identity_review");
  const caller = detail?.caller_name ?? null;
  const account = detail?.account_name ?? null;
  const callerDiffers = !!(
    review &&
    caller &&
    account &&
    caller.toLowerCase() !== account.toLowerCase()
  );
  const agTune = tags.includes("agreement_tuneup");
  const agMember = tags.includes("agreement_member");
  const paged = tags.includes("paged_overnight");
  const pagedName = t.affinity_empno ? empNames[t.affinity_empno] : null;

  const book = () => {
    // Open the SAME Schedule window as the board/360 — no bounce to the record
    // (Seth 2026-06-24). ASSIGN-AVAIL-01b: the needs_triage tag now clears in
    // the modal's onCreated (when the booking actually lands), NOT here — an
    // eager clear + a canceled window stranded the Lindell call on 10-13.
    onSchedule(t);
  };
  const notNeeded = () =>
    resolve.mutate({ id: t.id, action: "not_needed", tags });

  return (
    // Strict columns so every row lines up (Seth 2026-06-11: "misaligned
    // buttons… make the font look better"): dot | class chip (fixed width) |
    // content (name+tags line, reason line, signal line) | actions pinned
    // top-right at identical size and order in every row.
    <div className="group flex items-start gap-3 px-4 py-3.5">
      <span className="mt-[7px] shrink-0">
        <UrgencyDot u={t.urgency} />
      </span>
      <span
        className={cn(
          "mt-0.5 w-[118px] shrink-0 truncate rounded-full px-2 py-0.5 text-center text-[11px] font-semibold ring-1 ring-inset",
          meta.chip,
        )}
        title={meta.label}
      >
        {meta.label}
      </span>

      <div className="min-w-0 flex-1">
        {/* Line 1: who + status tags */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {t.custno ? (
            <CustomerLink
              custno={t.custno}
              title="Pull up this customer's record"
              className="text-sm font-semibold"
            >
              {leadName(t, custNames)}
            </CustomerLink>
          ) : (
            <span className="text-sm font-semibold text-slate-900">
              {t.customer_name}
            </span>
          )}
          {(agTune || agMember) && (
            <span className="rounded-full bg-emerald-50 px-1.5 py-px text-[11px] font-semibold text-emerald-700">
              {agTune ? "PLUS · from agreement" : "PLUS"}
            </span>
          )}
          {intent !== "book" && (
            <span className="rounded-full bg-amber-50 px-1.5 py-px text-[11px] font-semibold text-amber-700">
              {INTENT_CHIP[intent]}
              {refDate ? ` · ${refDate}` : ""}
            </span>
          )}
          {t.city && <span className="text-xs text-slate-400">{titleCaseIfShouty(t.city)}</span>}
        </div>

        {/* Line 2: the caller's own words — clamped so rows stay even. */}
        {reason && (
          <p
            className="mt-0.5 line-clamp-2 text-[13.5px] leading-snug text-slate-600"
            title={reason}
          >
            {reason}
          </p>
        )}

        {/* Line 3: signals + the full-call expander. */}
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
          {!t.custno && <span>New contact — not in the system</span>}
          {callerDiffers ? (
            <span className="inline-flex items-center gap-1 font-medium text-amber-700">
              <AlertTriangle className="h-3 w-3" />
              Called in as {caller} — possible new owner or tenant, verify
            </span>
          ) : (
            review &&
            t.custno && (
              <span className="inline-flex items-center gap-1 font-medium text-amber-700">
                <AlertTriangle className="h-3 w-3" />
                Name differs from the account — verify
              </span>
            )
          )}
          {(() => { const c = cameInChip(t); return c ? (
            <span className={cn("inline-flex items-center gap-1 rounded-full px-1.5 py-px font-semibold tabular-nums",
              c.hot ? "bg-red-50 text-red-600" : "bg-indigo-50 text-indigo-600")}>
              {c.label}
            </span>
          ) : null; })()}
          {paged && pagedName && (
            <span>Paged {pagedName} overnight — verify it was handled</span>
          )}
          {t.phone && <span className="tabular-nums">{t.phone}</span>}
          {detail && (
            <button
              type="button"
              onClick={() => setShowFull((v) => !v)}
              className="inline-flex items-center gap-0.5 font-medium text-brand-600 transition-colors hover:text-brand-700"
            >
              {showFull ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
              {showFull ? "Hide full call" : "See full call"}
            </button>
          )}
        </div>

        {/* What the tech already did with it, straight off the phone. */}
        {tags.includes("hold_for_office") && lastStamp(t) && (
          <p className="mt-1 rounded-md bg-amber-50/70 px-2 py-1 text-[12px] font-medium text-amber-900">
            {lastStamp(t)}
          </p>
        )}
        {showFull && detail && <FullCall d={detail} />}
        {resolve.isError && (
          <p className="mt-1 text-xs text-red-600">
            That didn&apos;t save — try again.
          </p>
        )}
      </div>

      {/* Actions: identical size, order, and top alignment in every row. */}
      <div className="flex shrink-0 items-center gap-1.5 pt-0.5">
        {intent === "book" ? (
          <button
            type="button"
            onClick={book}
            className="inline-flex h-8 w-[92px] items-center justify-center gap-1 rounded-lg bg-brand-700 text-[12.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
          >
            <CalendarPlus className="h-3 w-3" />
            Schedule
          </button>
        ) : (
          <span className="w-[92px]" aria-hidden />
        )}
        {t.custno ? (
          <button
            type="button"
            onClick={() => navigate(`/customers/${t.custno}`)}
            title="Pull up this customer's record"
            className={cn(
              "inline-flex h-8 w-[76px] items-center justify-center rounded-lg text-[12.5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
              intent === "book"
                ? "border border-hairline bg-white font-medium text-slate-600 hover:bg-slate-50"
                : "bg-brand-700 font-semibold text-white hover:bg-brand-800",
            )}
          >
            Qualify
          </button>
        ) : (
          <span className="w-[76px]" aria-hidden />
        )}
        <button
          type="button"
          onClick={notNeeded}
          disabled={resolve.isPending}
          title={
            intent === "book"
              ? "Customer fixed it, found someone else, or it started working — close with no service-history footprint (reversible)"
              : "Dismiss this lead — no action needed (reversible)"
          }
          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-300 transition-colors hover:bg-slate-100 hover:text-red-500 disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

/** The complete recorded call — everything the answering service took — so a
 *  dispatcher never has to open the PDF in their email (Seth 2026-06-13). */
function FullCall({ d }: { d: OncallDetail }) {
  const locality = [d.city, [d.state, d.zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  const address = [d.address, locality].filter(Boolean).join(" · ");
  const rows: [string, string | null | undefined][] = [
    ["Call type", d.call_type],
    ["Urgency", d.urgency_text],
    ["Service needed", d.service_needed],
    ["Caller", d.caller_name],
    [
      "On the account",
      d.account_name && d.account_name !== d.caller_name ? d.account_name : null,
    ],
    ["Phone", d.phone],
    ["Address", address || null],
    ["Taken", d.taken_at],
  ];
  const shown = rows.filter(([, v]) => v);
  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
      <div className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-[12px]">
        {shown.map(([k, v]) => (
          <Fragment key={k}>
            <span className="text-slate-400">{k}</span>
            <span className="text-slate-700">{v}</span>
          </Fragment>
        ))}
      </div>
      {d.message && (
        <p className="mt-2 border-t border-slate-200 pt-2 text-[13px] italic leading-snug text-slate-700">
          &ldquo;{d.message}&rdquo;
        </p>
      )}
    </div>
  );
}

function RanRow({
  t,
  empNames,
  custNames,
}: {
  t: PendingTicket;
  empNames: Record<string, string>;
  custNames: Map<string, string> | undefined;
}) {
  const resolve = useResolveOncall();
  const dismiss = useDismiss();
  const techName = t.affinity_empno ? empNames[t.affinity_empno] : null;
  return (
    <div className="group flex items-center gap-3 px-4 py-2">
      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
      <div className="min-w-0 flex-1 text-xs text-slate-600">
        {t.custno ? (
          <CustomerLink custno={t.custno} title="Pull up this customer's record">
            {leadName(t, custNames)}
          </CustomerLink>
        ) : (
          <span className="font-semibold text-slate-900">
            {t.customer_name}
          </span>
        )}
        <span> — {techName ?? "a tech"} ran this after-hours</span>
        {t.scheduled_dispatch && (
          <>
            {" · "}
            <DispatchLink dispatch={t.scheduled_dispatch} />
          </>
        )}
      </div>
      <button
        type="button"
        onClick={() => resolve.mutate({ id: t.id, action: "reopen", tags: t.tags })}
        title="Didn't actually happen? Put it back in the action list"
        className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-slate-500 opacity-0 transition-all hover:bg-white hover:text-slate-700 group-hover:opacity-100"
      >
        <Undo2 className="h-3 w-3" />
        Didn&apos;t happen?
      </button>
      <button
        type="button"
        onClick={() => dismiss.mutate(`oncallran:${t.id}`)}
        title="Got it — clear this from the morning view (the dispatch itself is untouched)"
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-300 transition-colors hover:bg-emerald-100 hover:text-emerald-700"
      >
        <Check className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ClosedSection({ items, empNames }: { items: PendingTicket[]; empNames: Record<string, string> }) {
  const [open, setOpen] = useState(false);
  const resolve = useResolveOncall();
  // WHO took care of it (Seth 2026-09-01): resolved_by is a tech empno from
  // the phone or an office email — show a name either way, plus when + why.
  const whoName = (t: PendingTicket) => {
    const by = t.resolved_by ?? "";
    if (!by) return null;
    if (empNames[by]) return empNames[by];
    if (by.includes("@")) return by.split("@")[0].replace(/[._-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    if (by.startsWith("auto:")) return "Pegasus (auto)";
    return by;
  };
  return (
    <div className="pt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[12px] font-medium text-slate-400 transition-colors hover:text-slate-600"
      >
        {open ? "Hide" : "Show"} {items.length} closed (not needed)
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {items.map((t) => (
            <div key={t.id} className="flex items-start gap-2 text-[12px] text-slate-500">
              <X className="mt-0.5 h-3 w-3 shrink-0 text-slate-300" />
              <div className="min-w-0 flex-1">
                <span className="font-medium text-slate-600 line-through">{t.customer_name}</span>
                <span className="ml-2 text-slate-400">
                  {whoName(t) ? `closed by ${whoName(t)}` : "closed"}
                  {t.resolved_at ? ` · ${new Date(t.resolved_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}
                </span>
                {lastStamp(t) && <p className="mt-0.5 truncate text-[11.5px] italic text-slate-400" title={lastStamp(t) ?? undefined}>{lastStamp(t)}</p>}
              </div>
              <button
                type="button"
                onClick={() =>
                  resolve.mutate({ id: t.id, action: "reopen", tags: t.tags })
                }
                className="shrink-0 text-brand-600 transition-colors hover:underline"
              >
                reopen
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UrgencyDot({ u }: { u: PendingTicket["urgency"] }) {
  const c =
    u === "asap" ? "bg-red-500" : u === "soon" ? "bg-amber-500" : "bg-slate-300";
  const label = u === "asap" ? "ASAP" : u === "soon" ? "Soon" : "Routine";
  return (
    <span
      className={cn("h-2 w-2 shrink-0 rounded-full", c)}
      title={label}
      aria-label={label}
    />
  );
}

function Pill({
  tone,
  children,
}: {
  tone: "amber" | "emerald" | "brand" | "slate";
  children: React.ReactNode;
}) {
  const c =
    tone === "amber"
      ? "bg-amber-100 text-amber-700"
      : tone === "emerald"
        ? "bg-emerald-100 text-emerald-700"
        : tone === "brand"
          ? "bg-brand-100 text-brand-700"
          : "bg-slate-100 text-slate-500";
  return (
    <span className={cn("rounded-full px-1.5 py-0.5 text-[11px] font-semibold", c)}>
      {children}
    </span>
  );
}
